import OTPInput from '@/components/OTPInput';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { AlternativeMethod, MfaType, REMEMBER_ME_OPTIONS, SignInFormType } from './types';

/**
 * Props for the MfaFields component
 */
interface MfaFieldsProps {
  control: Control<SignInFormType>;
  errors: FieldErrors<SignInFormType>;
  loading: boolean;
  mfaType: MfaType;
  onToggleMfaMode: () => void;
  alternativeMethods?: AlternativeMethod[];
  onRequestAlternativeOtp?: (method: string) => Promise<void>;
}

/**
 * Multi-Factor Authentication Fields Component
 * 
 * Renders the MFA verification fields including:
 * - Remember me duration selector
 * - OTP input for TOTP/email verification
 * - Recovery code input
 * - Toggle button to switch between OTP and recovery code
 * 
 * @component
 * @param {MfaFieldsProps} props - Component props
 * @returns {React.ReactElement} The MFA verification fields
 */
export function MfaFields({
  control,
  errors,
  loading,
  mfaType,
  onToggleMfaMode,
  alternativeMethods = [],
  onRequestAlternativeOtp,
}: MfaFieldsProps): React.ReactElement {
  return (
    <div className="space-y-16">
      <Controller
        name="remember_me_duration"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth>
            <InputLabel id="remember-me">
              2FA Remember Duration
            </InputLabel>
            <Select
              labelId="remember-me-label"
              id="remember-me"
              label="2FA Remember Duration"
              value={field.value}
              onChange={(e: SelectChangeEvent<number>) =>
                field.onChange(e.target.value)
              }
              {...field}
            >
              {REMEMBER_ME_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      {(mfaType === 'totp' || mfaType === 'email') && (
        <>
          <OTPInput
            control={control}
            name="otp"
            type={mfaType as 'totp' | 'email'}
            errorMessage={errors?.otp?.message as string}
            isSubmitting={loading}
            autoSubmit={false}
          />

          {/* Alternative method selection - only show if user has multiple methods configured */}
          {alternativeMethods.length > 0 && onRequestAlternativeOtp && (
            <div className="space-y-2">
              <Typography variant="body2" color="text.secondary" className="text-center">
                Don't have access to your {mfaType === 'totp' ? 'authenticator app' : 'email'}?
              </Typography>
              {alternativeMethods.map((altMethod) => (
                <Button
                  key={altMethod.method}
                  variant="text"
                  color="primary"
                  size="small"
                  fullWidth
                  disabled={loading}
                  onClick={() => onRequestAlternativeOtp(altMethod.method)}
                  className="text-sm normal-case"
                >
                  {loading ? (
                    <CircularProgress size={16} className="mr-2" />
                  ) : null}
                  Use {altMethod.display_name} instead
                </Button>
              ))}
            </div>
          )}
        </>
      )}

      {mfaType === 'recovery' && (
        <Controller
          name="recovery_code"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Recovery Code"
              type="text"
              error={!!errors.recovery_code}
              helperText={errors?.recovery_code?.message}
              variant="outlined"
              required
              fullWidth
            />
          )}
        />
      )}

      <Button
        className="float-right"
        onClick={onToggleMfaMode}
      >
        {mfaType === 'recovery' ? 'Switch back to OTP' : 'Use recovery code'}
      </Button>
    </div>
  );
}